import { Box, Button, FormHelperText, FormLabel, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HandleImageUpload } from '../../../app/AppUtils';
import { fetchAppSettings } from '../../settings/appSettingsSlice';
import { fetchSiomSettings } from '../../settings/siomSettingsSlice';
import { fetchStudentSettings } from '../../settings/studentSettingsSlice';
import useSearchDebounce from '../../subcomponents/SearchDebounce';
import { setAlertMessage, setAlertSeverity, setAlertStatus } from '../../subcomponents/alertSlice';
import { fetchExternalConnectionSettings, putImageSetting, resetUpdateStatus, setChangedThemeSettings } from '../adminSlice';

export default function AdminSettingImage({ settingsType, settingKey, formLabel, formText, xsSize, multilineEnabled, notEmpty, disabledCondition, departmentSetting }) {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { updateImageSetting } = useSelector((state) => state.admin);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { activeDepartment } = useSelector((state) => state.settings.theme);

    const [settingChanged, setSettingChanged] = useState(false);
    const [settingValue, setSettingValue] = useSearchDebounce();
    const [newImageName, setNewImageName] = useState(null);
    const [errorImage, setErrorImage] = useState(null);

    // Put Callback
    useEffect(() => {
        if (updateImageSetting.status === "success") {
            // Alert
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].admin.SETTING_CHANGED_SUCCESS}`));
            dispatch(setAlertSeverity("success"));

            // Reset status & values
            dispatch(resetUpdateStatus());
            setSettingChanged(false);
            setSettingValue(null);

            // Get new setting values
            if (settingsType === "app")
                dispatch(fetchAppSettings());

            if (settingsType === "siom")
                dispatch(fetchSiomSettings());

            if (settingsType === "student")
                dispatch(fetchStudentSettings());

            if (settingsType === "external")
                dispatch(fetchExternalConnectionSettings());

            if (settingsType === "theme")
                dispatch(setChangedThemeSettings(true));
        } else if (updateImageSetting.status === "failed") {
            // Alert
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].admin.SETTING_CHANGED_FAILED}`));
            dispatch(setAlertSeverity("error"));

            // Reset status
            dispatch(resetUpdateStatus());
        };
    }, [updateImageSetting.status]);

    function handleImageChange(event) {
        setErrorImage(null);
        var imagePromise = HandleImageUpload(event);

        imagePromise.then(
            (value) => {
                setNewImageName(value?.name);
                setSettingValue(value?.image);
                setSettingChanged(true);
            },
            (error) => {
                (error.message === "SIZE" && setErrorImage(resourceFinal[appLanguage].messages.ERROR_FILE_TOO_LARGE));
                (error.message === "TYPE" && setErrorImage(resourceFinal[appLanguage].messages.ERROR_FILE_NOT_IMAGE));
            });
    };

    useEffect(() => {
        if (settingValue !== null) {
            dispatch(putImageSetting({
                key: settingKey,
                imageUrl: settingValue,
                imageName: newImageName,
                modifiedBy: `AP/${user?.number}`,
                departmentId: siomSettings?.useMultipleDepartments ? activeDepartment[0].id : undefined,
                departmentSpecific: departmentSetting
            }));
        };
    }, [dispatch, user, settingValue, settingChanged]);

    return (
        <Grid size={{ xs: xsSize }} sx={{ display: 'flex' }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flex: '1 1 auto',
                border: 1,
                borderColor: 'white.dark',
                borderRadius: 1,
                bgcolor: 'white.light',
                p: 1
            }}>
                <FormLabel component="legend">{formLabel}</FormLabel>
                <Button
                    sx={{ width: 1, height: 1 }}
                    disabled={disabledCondition}
                    variant="outlined"
                    component="label"
                    color="secondary"
                >
                    {newImageName ? newImageName : resourceFinal[appLanguage].words.IMAGE}
                    <input
                        type="file"
                        name="image"
                        onChange={handleImageChange}
                        hidden
                    />
                </Button>
                <FormHelperText>{formText}</FormHelperText>
                <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{errorImage}</Typography>
            </Box>
        </Grid>
    );
}
